import React, { useState, useEffect } from 'react';
import WatconnectApi from '../../../api/WatconnectApi';
import DataApi from '../../../api/DataApi';
import SparkApi from '../../../api/SparkApi';
import ObjectConfigApi from '../../../api/ObjectConfigApi';
import './WhatsAppMessenger.css';

const TemplateModal = ({
  isOpen,
  onClose,
  recipientPhone,
  recipientName,
  selectedWhatsAppSetting,
  onSendComplete,
  campaignRecord,
  selectedConfig,
  initialTemplateName,
  initialParameters,
}) => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [parameterValues, setParameterValues] = useState({});
  const [templateChangeKey, setTemplateChangeKey] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileURL, setSelectedFileURL] = useState(null);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [objectFields, setObjectFields] = useState([]);
  const fileInputRef = React.useRef(null);

  const categories = ['ALL', 'MARKETING', 'UTILITY'];

  // File validation configurations
  // const FILE_VALIDATIONS = {
  //   DOCUMENT: {
  //     maxSize: 100 * 1024 * 1024,
  //     allowedTypes: [
  //       'text/plain',
  //       'application/vnd.ms-excel',
  //       'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  //       'application/msword',
  //       'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  //       'application/vnd.ms-powerpoint',
  //       'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  //       'application/pdf',
  //     ],
  //     allowedExtensions: [
  //       '.txt',
  //       '.xls',
  //       '.xlsx',
  //       '.doc',
  //       '.docx',
  //       '.ppt',
  //       '.pptx',
  //       '.pdf',
  //     ],
  //   },
  //   IMAGE: {
  //     maxSize: 5 * 1024 * 1024,
  //     allowedTypes: ['image/jpeg', 'image/png'],
  //     allowedExtensions: ['.jpg', '.jpeg', '.png'],
  //   },
  //   VIDEO: {
  //     maxSize: 16 * 1024 * 1024,
  //     allowedTypes: ['video/3gpp', 'video/mp4'],
  //     allowedExtensions: ['.3gp', '.mp4'],
  //   },
  // };
  const transformTemplate = template => {
    console.log('template', template);
    const components = [];

    const safeText = val => {
      if (val === null || val === 'null' || val === undefined) return '';
      if (typeof val === 'object') return val.text || JSON.stringify(val);
      return String(val || '');
    };

    const extractExampleValues = (text, exampleData) => {
      if (!text) return [];
      const regex = /\{\{(\d+)\}\}/g;
      const matches = [];
      let match;
      let paramIndexes = [];

      // First, collect all parameter indexes in order
      while ((match = regex.exec(text))) {
        paramIndexes.push(parseInt(match[1]));
      }

      // Then assign example values based on parameter indexes
      paramIndexes.forEach((paramIndex, arrayIndex) => {
        // Use provided example data if available, otherwise use generic placeholder
        if (exampleData && exampleData[arrayIndex] !== undefined) {
          matches.push(exampleData[arrayIndex]);
        } else {
          matches.push(`value${paramIndex}`);
        }
      });

      return matches;
    };

    // 🖼️ HEADER — handle image/video/document or plain text header
    if (template.header && template.header !== 'null') {
      const headerComponent = {
        type: 'HEADER',
        format: template.header.toUpperCase(),
      };

      // If image/video/document header => use file_data
      if (
        ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(
          template.header.toUpperCase()
        ) &&
        template.file_data
      ) {
        headerComponent.file = {
          url: template.file_data.file_url || '',
          name: template.file_data.file_name || '',
          mime_type: template.file_data.mime_type || '',
        };
      } else if (template.header_body && template.header_body !== 'null') {
        headerComponent.text = safeText(template.header_body);
        headerComponent.example = {
          header_text: extractExampleValues(template.header_body),
        };
      }

      components.push(headerComponent);
    }

    if (template.message_body) {
      // Use stored example_body_text from database
      let exampleValues = [];
      if (
        template.example_body_text &&
        Array.isArray(template.example_body_text)
      ) {
        exampleValues = template.example_body_text;
      }

      components.push({
        type: 'BODY',
        text: safeText(template.message_body),
        example: {
          body_text: [
            extractExampleValues(template.message_body, exampleValues),
          ],
        },
      });
    }

    // 🦶 FOOTER
    if (template.footer && template.footer !== 'null') {
      components.push({
        type: 'FOOTER',
        text: safeText(template.footer),
      });
    }

    // 🔘 BUTTONS (normal templates only)
    if (Array.isArray(template.buttons) && template.buttons.length > 0) {
      components.push({
        type: 'BUTTONS',
        buttons: template.buttons.map(btn => ({
          type: btn.type,
          text: btn.text,
          url: btn.url || undefined,
          phone_number: btn.phone_number || undefined,
        })),
      });
    }

    // 🎠 CAROUSEL templates - FIXED VERSION
    if (Array.isArray(template.cards) && template.cards.length > 0) {
      const carouselCards = template.cards.map(card => {
        const cardComponents = [];

        // Header (image or video)
        if (card.header) {
          cardComponents.push({
            type: 'HEADER',
            format: card.header.format?.toUpperCase() || 'IMAGE',
            handle: card.header.handle || '',
          });
        }

        // Body with proper example values
        if (card.body) {
          const bodyText = safeText(card.body.text);
          let exampleValues = [];

          // Extract example values from card.body.example if available
          if (card.body.example && Array.isArray(card.body.example)) {
            exampleValues = card.body.example;
          }

          cardComponents.push({
            type: 'BODY',
            text: bodyText,
            example: {
              body_text: [extractExampleValues(bodyText, exampleValues)],
            },
          });
        }

        // Buttons (CTA / Quick replies)
        if (Array.isArray(card.buttons) && card.buttons.length > 0) {
          cardComponents.push({
            type: 'BUTTONS',
            buttons: card.buttons.map(btn => ({
              type: btn.type,
              text: btn.text,
              url: btn.url || '',
              phone_number: btn.phone_number || '',
            })),
          });
        }

        return { components: cardComponents };
      });

      components.push({
        type: 'CAROUSEL',
        cards: carouselCards,
      });
    }

    // 🧾 Return final unified structure
    return {
      id: template.id,
      name: template.template_name,
      category: template.category,
      language: template.language,
      status: template.status,
      template_type: template.template_type,
      components,
      // Keep original file_data for reference
      file_data: template.file_data,
      example_body_text: template.example_body_text,
    };
  };
  const objectName = selectedConfig?.object_name || '';

  useEffect(() => {
    const fetchObjectFields = async () => {
      if (!objectName) {
        setObjectFields([]);
        return;
      }
      try {
        console.log(`[TemplateModal] Fetching fields for object: ${objectName}`);
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
        if (response && response.success && response.data?.fields) {
          setObjectFields(response.data.fields || []);
        } else if (response && response.fields) {
          setObjectFields(response.fields || []);
        } else if (Array.isArray(response)) {
          setObjectFields(response);
        } else {
          setObjectFields([]);
        }
      } catch (err) {
        console.error('[TemplateModal] Error fetching object fields:', err);
        setObjectFields([]);
      }
    };
    fetchObjectFields();
  }, [objectName]);

  const resolveParamValue = (val, clientName, paramKey = '') => {
    if (paramKey && parameterValues[`${paramKey}_field`]) {
      const fieldName = parameterValues[`${paramKey}_field`];
      const fallback = parameterValues[`${paramKey}_fallback`] || '';
      return fallback
        ? `[Field: ${fieldName} | Fallback: "${fallback}"]`
        : `[Field: ${fieldName}]`;
    }
    if (!val) {
      if (paramKey && parameterValues[`${paramKey}_fallback`]) {
        return parameterValues[`${paramKey}_fallback`];
      }
      return '';
    }
    const trimmed = String(val).trim();
    const lower = trimmed.toLowerCase();
    if (
      lower === 'name' ||
      lower === '{{name}}' ||
      lower === '{name}' ||
      lower === 'client_name' ||
      lower === '{{client_name}}' ||
      lower === 'client name' ||
      lower === '{{client name}}'
    ) {
      return clientName || val;
    }
    return val;
  };

  useEffect(() => {
    if (isOpen) {
      setSending(false);
      fetchTemplates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedCategory === 'ALL') {
      setFilteredTemplates(templates);
    } else {
      setFilteredTemplates(
        templates.filter(template => template.category === selectedCategory)
      );
    }

    if (templates.length > 0 && initialTemplateName) {
      const matched = templates.find(
        t => t.name === initialTemplateName || t.id === initialTemplateName
      );
      if (matched) {
        if (initialParameters && typeof initialParameters === 'object' && Object.keys(initialParameters).length > 0) {
          setSelectedTemplate(matched);
          setParameterValues(initialParameters);
        } else {
          handleTemplateSelect(matched);
        }
        return;
      }
    }

    setSelectedTemplate(null);
    setParameterValues({});
    setSelectedFile(null);
    setSelectedFileURL(null);
    setSelectedFileType(null);
    setErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [templates, selectedCategory, initialTemplateName, initialParameters]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const dataApi = new DataApi('watconnect');
      const res = await dataApi.fetchById(
        `watconnect_template?business_number=${selectedWhatsAppSetting}`
      );

      if (res?.data?.data && Array.isArray(res.data?.data)) {
        console.log('Raw templates:', res.data?.data);

        // Transform templates, filter only APPROVED ones, and sort by createddate DESC (newest first)
        const transformedTemplates = res.data.data
          .filter(t => t.status === 'APPROVED')
          .sort((a, b) => {
            const dateA = a.createddate ? new Date(a.createddate) : new Date(0);
            const dateB = b.createddate ? new Date(b.createddate) : new Date(0);
            return dateB - dateA;
          })
          .map(transformTemplate);

        console.log('Transformed templates:', transformedTemplates);
        setTemplates(transformedTemplates);
      } else {
        setTemplates([]);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // const validateFile = (file, format) => {
  //   const validation =
  //     FILE_VALIDATIONS[
  //       format === 'IMAGE' ? 'IMAGE' : format === 'VIDEO' ? 'VIDEO' : 'DOCUMENT'
  //     ];

  //   if (!validation) {
  //     return { isValid: false, error: `Unsupported format: ${format}` };
  //   }

  //   if (file.size > validation.maxSize) {
  //     const maxSizeMB = validation.maxSize / (1024 * 1024);
  //     return {
  //       isValid: false,
  //       error: `File size too large. Maximum allowed: ${maxSizeMB}MB`,
  //     };
  //   }

  //   if (!validation.allowedTypes.includes(file.type)) {
  //     return {
  //       isValid: false,
  //       error: `Invalid file type. Allowed types: ${validation.allowedExtensions.join(', ')}`,
  //     };
  //   }

  //   const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
  //   if (!validation.allowedExtensions.includes(fileExtension)) {
  //     return {
  //       isValid: false,
  //       error: `Invalid file extension. Allowed: ${validation.allowedExtensions.join(', ')}`,
  //     };
  //   }

  //   return { isValid: true, error: '' };
  // };

  const handleTemplateSelect = template => {
    setSelectedFile(null);
    setSelectedFileURL(null);
    setSelectedFileType(null);
    setErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (!template) {
      setSelectedTemplate(null);
      setParameterValues({});
      setTemplateChangeKey(prev => prev + 1);
      return;
    }

    setSelectedTemplate(template);
    setParameterValues({});
    setTemplateChangeKey(prev => prev + 1);

    const initialParams = {};
    template.components?.forEach(component => {
      if (component.type === 'BODY') {
        const exampleValues = component.example?.body_text?.[0] || [];
        const params = extractParameters(component.text);

        params.forEach((paramIndex, arrayIndex) => {
          const exampleVal = exampleValues[arrayIndex] || '';

          if (paramIndex === 1) {
            if (recipientName) {
              initialParams[`body_${paramIndex}`] = recipientName;
              initialParams[`body_${paramIndex}_fallback`] = recipientName;
            } else if (exampleVal) {
              initialParams[`body_${paramIndex}_fallback`] = exampleVal;
            }
          } else if (exampleVal) {
            initialParams[`body_${paramIndex}_fallback`] = exampleVal;
          }
        });
      }
    });

    template.components?.forEach(component => {
      if (component.type === 'CAROUSEL') {
        component.cards?.forEach((card, cardIndex) => {
          card.components?.forEach(cardComp => {
            if (cardComp.type === 'BODY') {
              const exampleValues = cardComp.example?.body_text?.[0] || [];
              const params = extractParameters(cardComp.text);

              params.forEach((paramIndex, arrayIndex) => {
                const exampleVal = exampleValues[arrayIndex] || '';

                if (paramIndex === 1) {
                  if (recipientName) {
                    initialParams[`carousel_${cardIndex}_${paramIndex}`] = recipientName;
                    initialParams[`carousel_${cardIndex}_${paramIndex}_fallback`] = recipientName;
                  } else if (exampleVal) {
                    initialParams[`carousel_${cardIndex}_${paramIndex}_fallback`] = exampleVal;
                  }
                } else if (exampleVal) {
                  initialParams[`carousel_${cardIndex}_${paramIndex}_fallback`] = exampleVal;
                }
              });
            }
          });
        });
      }
    });

    setParameterValues(initialParams);
  };

  const handleParameterChange = (paramIndex, value) => {
    setParameterValues(prev => ({
      ...prev,
      [paramIndex]: value,
    }));
  };

  const extractParameters = text => {
    if (!text) return [];
    const paramRegex = /\{\{(\d+)\}\}/g;
    const params = [];
    let match;
    while ((match = paramRegex.exec(text)) !== null) {
      params.push(parseInt(match[1]));
    }
    return [...new Set(params)].sort((a, b) => a - b);
  };

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (!file) return;

    const fileType = file.type;
    const fileSize = file.size;

    const headerComp = selectedTemplate.components?.find(
      comp => comp.type === 'HEADER'
    );
    const headerFormat = headerComp ? headerComp.format : '';

    let allowedMimeTypes = [];
    let allowedExtensions = [];
    let maxSize = 0;

    if (headerFormat === 'IMAGE') {
      allowedMimeTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      allowedExtensions = ['.jpeg', '.jpg', '.png'];
      maxSize = 5 * 1024 * 1024; // 5MB
    } else if (headerFormat === 'VIDEO') {
      allowedMimeTypes = ['video/mp4'];
      allowedExtensions = ['.mp4'];
      maxSize = 16 * 1024 * 1024; // 16MB
    } else if (headerFormat === 'DOCUMENT') {
      allowedMimeTypes = [
        'application/pdf',
        'text/plain',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ];
      allowedExtensions = [
        '.pdf',
        '.txt',
        '.xls',
        '.xlsx',
        '.doc',
        '.docx',
        '.ppt',
        '.pptx',
      ];
      maxSize = 100 * 1024 * 1024; // 100MB
    } else if (headerFormat === 'AUDIO') {
      allowedMimeTypes = [
        'audio/aac',
        'audio/mp4',
        'audio/amr',
        'audio/mpeg',
        'audio/ogg',
      ];
      allowedExtensions = ['.aac', '.mp4', '.amr', '.mp3', '.mpeg', '.ogg'];
      maxSize = 16 * 1024 * 1024; // 16MB
    }

    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    // Validate file type or extension
    const isMimeAllowed = allowedMimeTypes.includes(fileType);
    const isExtAllowed = allowedExtensions.includes(fileExtension);

    if (!isMimeAllowed && !isExtAllowed) {
      setErrorMessage(
        `Invalid file type. Allowed: ${allowedExtensions.join(', ')}`
      );
      setSelectedFile(null);
      setSelectedFileURL(null);
      return;
    }

    // Validate file size
    if (fileSize > maxSize) {
      setErrorMessage(
        `File size exceeds the limit (${maxSize / (1024 * 1024)}MB).`
      );
      setSelectedFile(null);
      setSelectedFileURL(null);
      return;
    }

    setErrorMessage('');

    setSelectedFile(file);
    setSelectedFileURL(URL.createObjectURL(file));
  };

  const areAllParametersFilled = () => {
    if (!selectedTemplate) return false;

    // Check header file if template has IMAGE/VIDEO/DOCUMENT header format
    const headerComp = selectedTemplate.components?.find(
      comp => comp.type === 'HEADER'
    );
    if (
      headerComp &&
      ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComp.format)
    ) {
      if (!selectedFile || errorMessage) {
        return false;
      }
    }

    // Check body parameters - Fallback value is mandatory
    const bodyComponents =
      selectedTemplate.components?.filter(comp => comp.type === 'BODY') || [];
    for (const component of bodyComponents) {
      const params = extractParameters(component.text);
      for (const param of params) {
        const fallbackVal = parameterValues[`body_${param}_fallback`]?.trim();
        if (!fallbackVal) {
          return false;
        }
      }
    }

    // Check carousel body parameters - Fallback value is mandatory
    const carouselComponents =
      selectedTemplate.components?.filter(comp => comp.type === 'CAROUSEL') ||
      [];
    for (const carousel of carouselComponents) {
      for (
        let cardIndex = 0;
        cardIndex < (carousel.cards?.length || 0);
        cardIndex++
      ) {
        const card = carousel.cards[cardIndex];
        for (const cardComp of card.components || []) {
          if (cardComp.type === 'BODY') {
            const params = extractParameters(cardComp.text);
            for (const param of params) {
              const fallbackVal = parameterValues[`carousel_${cardIndex}_${param}_fallback`]?.trim();
              if (!fallbackVal) {
                return false;
              }
            }
          }
        }
      }
    }

    return true;
  };
  const renderParameterInputs = () => {
    if (!selectedTemplate) return null;

    const inputs = [];

    // Body parameters only
    const bodyComponents =
      selectedTemplate.components?.filter(comp => comp.type === 'BODY') || [];
    bodyComponents.forEach((component, compIndex) => {
      const params = extractParameters(component.text);
      const exampleValues = component.example?.body_text?.[0] || [];

      params.forEach((paramIndex, arrayIndex) => {
        const example = exampleValues[arrayIndex] || `value${paramIndex}`;

        const fieldKey = `body_${paramIndex}_field`;
        const fallbackKey = `body_${paramIndex}_fallback`;
        const valueKey = `body_${paramIndex}`;

        const currentField = parameterValues[fieldKey] || '';
        const currentFallback = parameterValues[fallbackKey] || '';

        const isFallbackMissing = !currentFallback?.trim();

        inputs.push(
          <div key={`body-param-${paramIndex}`} className='parameter-input mb-3 p-3 border rounded bg-light'>
            <div className='fw-bold mb-2 text-dark border-bottom pb-1 d-flex justify-content-between align-items-center'>
              <span>
                Body Parameter {paramIndex}{' '}
                <span className='example-text text-muted fw-normal' style={{ fontSize: '12px' }}>(e.g., {example})</span>
              </span>
              <span className='badge bg-warning text-dark'>Fallback Required</span>
            </div>

            {/* Object Field Selection (Optional - Campaign mode only) */}
            {objectName && objectFields.length > 0 && (
              <div className='mb-3'>
                <label className='form-label mb-1 text-dark' style={{ fontSize: '13px', fontWeight: 600 }}>
                  <i className='fa-solid fa-list-check me-1 text-primary'></i>
                  Select {selectedConfig?.name || selectedConfig?.object_name || 'Object'} Field (Optional):
                </label>
                <select
                  className='form-select form-select-sm'
                  value={currentField}
                  onChange={e => {
                    const selectedField = e.target.value;
                    handleParameterChange(fieldKey, selectedField);
                    if (selectedField) {
                      handleParameterChange(valueKey, `{{${selectedField}}}`);
                    } else {
                      handleParameterChange(valueKey, '');
                    }
                  }}
                >
                  <option value=''>-- Select Field to Bind --</option>
                  {objectFields.map((fieldItem, fIdx) => (
                    <option key={fieldItem.id || fieldItem.name || fIdx} value={fieldItem.name || fieldItem.field_name}>
                      {fieldItem.label || fieldItem.field_label || fieldItem.name} ({fieldItem.name || fieldItem.field_name})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Fallback Value Input (Mandatory) */}
            <div className='mb-2'>
              <label className='form-label mb-1 text-dark' style={{ fontSize: '13px', fontWeight: 600 }}>
                <i className='fa-solid fa-shield-halved me-1 text-warning'></i>
                Fallback Value <span className='text-danger'>*</span>:
              </label>
              <input
                type='text'
                value={currentFallback}
                onChange={e => handleParameterChange(fallbackKey, e.target.value)}
                placeholder='e.g. Customer, Valued Client, etc.'
                className={`form-control form-control-sm ${isFallbackMissing ? 'is-invalid border-danger' : ''}`}
              />
              {isFallbackMissing ? (
                <small className='text-danger d-block mt-1' style={{ fontSize: '11px' }}>
                  Fallback value is required.
                </small>
              ) : (
                <small className='text-muted d-block mt-1' style={{ fontSize: '11px' }}>
                  <i className='fa-solid fa-circle-info me-1 text-info'></i>
                  If any recipient field is empty (or no field selected), this fallback value will be used.
                </small>
              )}
            </div>
          </div>
        );
      });
    });

    // Carousel body parameters only
    const carouselComponents =
      selectedTemplate.components?.filter(comp => comp.type === 'CAROUSEL') ||
      [];
    carouselComponents.forEach(carousel => {
      carousel.cards?.forEach((card, cardIndex) => {
        inputs.push(
          <div
            key={`carousel-${cardIndex}-header`}
            className='carousel-section-header'
          >
            <h6>Carousel Card {cardIndex + 1}</h6>
          </div>
        );

        card.components?.forEach(cardComp => {
          if (cardComp.type === 'BODY') {
            const params = extractParameters(cardComp.text);
            const exampleValues = cardComp.example?.body_text?.[0] || [];

            params.forEach((paramIndex, arrayIndex) => {
              const example = exampleValues[arrayIndex] || `value${paramIndex}`;
              const fieldKey = `carousel_${cardIndex}_${paramIndex}_field`;
              const fallbackKey = `carousel_${cardIndex}_${paramIndex}_fallback`;
              const valueKey = `carousel_${cardIndex}_${paramIndex}`;

              const currentField = parameterValues[fieldKey] || '';
              const currentFallback = parameterValues[fallbackKey] || '';

              const isFallbackMissing = !currentFallback?.trim();

              inputs.push(
                <div
                  key={`carousel-${cardIndex}-param-${paramIndex}`}
                  className='parameter-input mb-3 p-3 border rounded bg-light'
                >
                  <div className='fw-bold mb-2 text-dark border-bottom pb-1 d-flex justify-content-between align-items-center'>
                    <span>
                      Card {cardIndex + 1} - Parameter {paramIndex}{' '}
                      <span className='example-text text-muted fw-normal' style={{ fontSize: '12px' }}>(e.g., {example})</span>
                    </span>
                    <span className='badge bg-warning text-dark'>Fallback Required</span>
                  </div>

                  {objectName && objectFields.length > 0 && (
                    <div className='mb-3'>
                      <label className='form-label mb-1 text-dark' style={{ fontSize: '13px', fontWeight: 600 }}>
                        <i className='fa-solid fa-list-check me-1 text-primary'></i>
                        Select {selectedConfig?.name || selectedConfig?.object_name || 'Object'} Field (Optional):
                      </label>
                      <select
                        className='form-select form-select-sm'
                        value={currentField}
                        onChange={e => {
                          const selectedField = e.target.value;
                          handleParameterChange(fieldKey, selectedField);
                          if (selectedField) {
                            handleParameterChange(valueKey, `{{${selectedField}}}`);
                          } else {
                            handleParameterChange(valueKey, '');
                          }
                        }}
                      >
                        <option value=''>-- Select Field to Bind --</option>
                        {objectFields.map((fieldItem, fIdx) => (
                          <option key={fieldItem.id || fieldItem.name || fIdx} value={fieldItem.name || fieldItem.field_name}>
                            {fieldItem.label || fieldItem.field_label || fieldItem.name} ({fieldItem.name || fieldItem.field_name})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className='mb-2'>
                    <label className='form-label mb-1 text-dark' style={{ fontSize: '13px', fontWeight: 600 }}>
                      <i className='fa-solid fa-shield-halved me-1 text-warning'></i>
                      Fallback Value <span className='text-danger'>*</span>:
                    </label>
                    <input
                      type='text'
                      value={currentFallback}
                      onChange={e => handleParameterChange(fallbackKey, e.target.value)}
                      placeholder='e.g. Customer, Valued Client, etc.'
                      className={`form-control form-control-sm ${isFallbackMissing ? 'is-invalid border-danger' : ''}`}
                    />
                    {isFallbackMissing ? (
                      <small className='text-danger d-block mt-1' style={{ fontSize: '11px' }}>
                        Fallback value is required.
                      </small>
                    ) : (
                      <small className='text-muted d-block mt-1' style={{ fontSize: '11px' }}>
                        <i className='fa-solid fa-circle-info me-1 text-info'></i>
                        If any recipient field is empty (or no field selected), this fallback value will be used.
                      </small>
                    )}
                  </div>
                </div>
              );
            });
          }
        });
      });
    });

    const headerComp = selectedTemplate.components?.find(
      comp => comp.type === 'HEADER'
    );
    if (
      headerComp &&
      ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerComp.format)
    ) {
      inputs.push(
        <div key='header-file-upload' className='parameter-input'>
          <label className='fw-bold text-dark'>
            Upload Header File ({headerComp.format}):{' '}
            <span className='text-danger'>*</span>
          </label>
          <input
            type='file'
            ref={fileInputRef}
            className='form-control border rounded'
            onChange={handleFileChange}
            accept={
              headerComp.format === 'IMAGE'
                ? 'image/jpeg,image/jpg,image/png'
                : headerComp.format === 'VIDEO'
                  ? 'video/mp4'
                  : '.pdf,.txt,.xls,.xlsx,.doc,.docx,.ppt,.pptx'
            }
          />
          {selectedFile && (
            <div className='mt-1 text-success'>
              Selected: <b>{selectedFile.name}</b>
            </div>
          )}
          {errorMessage && (
            <div className='text-danger mt-1'>{errorMessage}</div>
          )}
        </div>
      );
    }

    return inputs;
  };
  const renderWhatsAppPreview = () => {
    if (!selectedTemplate) return null;

    // Debug: Check what's in file_data
    console.log('Full selectedTemplate:', selectedTemplate);
    console.log('File data:', selectedTemplate.file_data);
    console.log('File URL:', selectedTemplate.file_data?.file_url);

    return (
      <div className='whatsapp-preview'>
        <div className='template-message'>
          {selectedTemplate.components?.map((component, index) => {
            if (component.type === 'HEADER') {
              if (
                component.format === 'IMAGE' ||
                component.format === 'VIDEO' ||
                component.format === 'DOCUMENT' // Added DOCUMENT here
              ) {
                const fileUrl = selectedFileURL;

                console.log('Using file URL:', fileUrl); // Debug

                return (
                  <div key={index} className='template-header'>
                    {fileUrl ? (
                      <div className='existing-file-preview'>
                        {component.format === 'IMAGE' ? (
                          <img
                            key={fileUrl}
                            src={fileUrl}
                            alt='Template header'
                            className='template-header-image'
                            onError={e => {
                              console.error(
                                'Image failed to load:',
                                fileUrl,
                                e
                              );
                              e.target.style.display = 'none';
                              const fallback = e.target.nextSibling;
                              if (fallback) fallback.style.display = 'block';
                            }}
                            onLoad={() =>
                              console.log('Image loaded successfully:', fileUrl)
                            }
                          />
                        ) : component.format === 'VIDEO' ? (
                          <video
                            key={fileUrl}
                            controls
                            className='template-header-video'
                            onError={e => {
                              console.error(
                                'Video failed to load:',
                                fileUrl,
                                e
                              );
                              e.target.style.display = 'none';
                              const fallback = e.target.nextSibling;
                              if (fallback) fallback.style.display = 'block';
                            }}
                          >
                            <source
                              src={fileUrl}
                              type={
                                selectedFile?.type ||
                                selectedTemplate.file_data?.mime_type
                              }
                            />
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          // DOCUMENT preview
                          <div className='document-preview'>
                            <div className='document-icon'>📄</div>
                            <div className='document-info'>
                              <div className='document-name'>
                                {selectedFile?.name ||
                                  selectedTemplate.file_data?.file_name ||
                                  'Document'}
                              </div>
                              <div className='document-size'>
                                {selectedFile
                                  ? `(${(selectedFile.size / 1024).toFixed(1)} KB)`
                                  : selectedTemplate.file_data?.file_size
                                    ? `(${(selectedTemplate.file_data.file_size / 1024).toFixed(1)} KB)`
                                    : '(Document)'}
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Fallback if media fails to load */}
                        <div
                          className='media-preview-placeholder'
                          style={{ display: 'none' }}
                        >
                          {component.format === 'IMAGE'
                            ? '🖼️'
                            : component.format === 'VIDEO'
                              ? '🎥'
                              : '📄'}{' '}
                          Media
                        </div>
                      </div>
                    ) : (
                      <div className='media-preview-placeholder'>
                        {component.format === 'IMAGE'
                          ? '🖼️ Image Header'
                          : component.format === 'VIDEO'
                            ? '🎥 Video Header'
                            : '📄 Document Header'}
                      </div>
                    )}
                  </div>
                );
              } else if (component.text) {
                return (
                  <div key={index} className='template-header'>
                    <div className='template-header-text text-dark fw-semibold'>
                      {typeof component.text === 'string'
                        ? component.text
                        : JSON.stringify(component.text)}
                    </div>
                  </div>
                );
              }
            }
            return null;
          })}

          {/* Rest of the BODY, CAROUSEL, FOOTER, and BUTTONS rendering remains the same */}
          {selectedTemplate.components?.map((component, index) => {
            if (component.type === 'BODY') {
              let processedText = component.text;
              extractParameters(component.text).forEach(param => {
                const rawValue = parameterValues[`body_${param}`];
                const value = resolveParamValue(rawValue, recipientName);
                if (value) {
                  processedText = processedText.replace(
                    new RegExp(`\\{\\{${param}\\}\\}`, 'g'),
                    value
                  );
                } else {
                  processedText = processedText.replace(
                    new RegExp(`\\{\\{${param}\\}\\}`, 'g'),
                    `{{${param}}}`
                  );
                }
              });
              return (
                <div key={index} className='template-body'>
                  <div>
                    {processedText
                      .split(/(\{\{\d+\}\})/)
                      .map((part, i) =>
                        part.match(/\{\{\d+\}\}/) ? <b key={i}>{part}</b> : part
                      )}
                  </div>
                </div>
              );
            }
            return null;
          })}

          {selectedTemplate.components?.map((component, index) => {
            if (component.type === 'CAROUSEL') {
              return (
                <div key={index} className='template-carousel'>
                  <div className='carousel-container'>
                    {component.cards?.map((card, cardIndex) => (
                      <div key={cardIndex} className='carousel-card'>
                        {card.components?.map((cardComp, compIndex) => {
                          if (
                            cardComp.type === 'HEADER' &&
                            (cardComp.format === 'IMAGE' ||
                              cardComp.format === 'VIDEO' ||
                              cardComp.format === 'DOCUMENT') // Added DOCUMENT here too
                          ) {
                            // For carousel cards, check if there's file data available
                            const hasFileData =
                              cardComp.file?.url ||
                              selectedTemplate.file_data?.file_url;

                            return (
                              <div key={compIndex} className='carousel-header'>
                                {hasFileData ? (
                                  <div className='existing-file-preview'>
                                    {cardComp.format === 'IMAGE' ? (
                                      <img
                                        src={
                                          cardComp.file?.url ||
                                          selectedTemplate.file_data.file_url
                                        }
                                        alt={`Card ${cardIndex + 1}`}
                                        className='carousel-header-image'
                                        onError={e => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display =
                                            'block';
                                        }}
                                      />
                                    ) : cardComp.format === 'VIDEO' ? (
                                      <video
                                        controls
                                        className='carousel-header-video'
                                        onError={e => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display =
                                            'block';
                                        }}
                                      >
                                        <source
                                          src={
                                            cardComp.file?.url ||
                                            selectedTemplate.file_data.file_url
                                          }
                                          type={
                                            cardComp.file?.mime_type ||
                                            selectedTemplate.file_data.mime_type
                                          }
                                        />
                                        Your browser does not support the video
                                        tag.
                                      </video>
                                    ) : (
                                      // DOCUMENT preview for carousel
                                      <div className='document-preview'>
                                        <div className='document-icon'>📄</div>
                                        <div className='document-info'>
                                          <div className='document-name'>
                                            {selectedTemplate.file_data
                                              ?.file_name || 'Document'}
                                          </div>
                                          <div className='document-size'>
                                            {selectedTemplate.file_data
                                              ?.file_size
                                              ? `(${(selectedTemplate.file_data.file_size / 1024).toFixed(1)} KB)`
                                              : '(Document)'}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                    {/* Fallback if media fails to load */}
                                    <div
                                      className='media-preview-placeholder'
                                      style={{ display: 'none' }}
                                    >
                                      {cardComp.format === 'IMAGE'
                                        ? '🖼️'
                                        : cardComp.format === 'VIDEO'
                                          ? '🎥'
                                          : '📄'}{' '}
                                      Media
                                    </div>
                                  </div>
                                ) : (
                                  <div className='media-preview-placeholder'>
                                    {cardComp.format === 'IMAGE'
                                      ? '🖼️'
                                      : cardComp.format === 'VIDEO'
                                        ? '🎥'
                                        : '📄'}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })}

                        {card.components?.map((cardComp, compIndex) => {
                          if (cardComp.type === 'BODY') {
                            let processedText = cardComp.text;
                            extractParameters(cardComp.text).forEach(param => {
                              const value =
                                parameterValues[
                                `carousel_${cardIndex}_${param}`
                                ];
                              if (value) {
                                processedText = processedText.replace(
                                  new RegExp(`\\{\\{${param}\\}\\}`, 'g'),
                                  value
                                );
                              } else {
                                processedText = processedText.replace(
                                  new RegExp(`\\{\\{${param}\\}\\}`, 'g'),
                                  `{{${param}}}`
                                );
                              }
                            });
                            return (
                              <div key={compIndex} className='carousel-body'>
                                {typeof processedText === 'string'
                                  ? processedText
                                  : JSON.stringify(processedText)}
                              </div>
                            );
                          }
                          return null;
                        })}

                        {card.components?.map((cardComp, compIndex) => {
                          if (cardComp.type === 'BUTTONS') {
                            return (
                              <div key={compIndex} className='carousel-buttons'>
                                {cardComp.buttons?.map((btn, btnIdx) => (
                                  <button
                                    key={btnIdx}
                                    className='carousel-button'
                                    disabled
                                  >
                                    {btn.text}
                                  </button>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })}

          {selectedTemplate.components?.map((component, index) => {
            if (component.type === 'FOOTER') {
              return (
                <div key={index} className='template-body text-muted'>
                  {typeof component.text === 'string'
                    ? component.text
                    : JSON.stringify(component.text)}
                </div>
              );
            }
            return null;
          })}

          {selectedTemplate.components?.map((component, index) => {
            if (component.type === 'BUTTONS') {
              return (
                <div key={index} className='template-buttons'>
                  {component.buttons?.map((button, btnIndex) => {
                    let buttonClass = 'template-button';
                    if (button.type === 'PHONE_NUMBER') buttonClass += ' phone';
                    else if (button.type === 'URL') buttonClass += ' url';
                    else buttonClass += ' primary';

                    return (
                      <button key={btnIndex} className={buttonClass} disabled>
                        {button.text}
                      </button>
                    );
                  })}
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
    );
  };
  const handleSendTemplate = async () => {
    if (!selectedTemplate || !areAllParametersFilled()) {
      alert(
        'Please enter a fallback value for all required parameters.'
      );
      return;
    }

    setSending(true);

    try {
      let finalParams = { ...parameterValues };
      
      // Set parameter value to {{fieldname}} when a field is selected
      const bodyComponents = selectedTemplate.components?.filter(comp => comp.type === 'BODY') || [];
      bodyComponents.forEach(component => {
        const params = extractParameters(component.text);
        params.forEach(param => {
          const fieldName = parameterValues[`body_${param}_field`];
          if (fieldName) {
            finalParams[`body_${param}`] = `{{${fieldName}}}`;
          }
        });
      });

      const carouselComponents = selectedTemplate.components?.filter(comp => comp.type === 'CAROUSEL') || [];
      carouselComponents.forEach(carousel => {
        carousel.cards?.forEach((card, cardIndex) => {
          card.components?.forEach(cardComp => {
            if (cardComp.type === 'BODY') {
              const params = extractParameters(cardComp.text);
              params.forEach(param => {
                const fieldName = parameterValues[`carousel_${cardIndex}_${param}_field`];
                if (fieldName) {
                  finalParams[`carousel_${cardIndex}_${param}`] = `{{${fieldName}}}`;
                }
              });
            }
          });
        });
      });
      if (selectedFile) {
        const attachmentsApi = new DataApi('attachments');
        const fileForm = new FormData();
        if (
          selectedFile.length !== undefined &&
          typeof selectedFile !== 'string'
        ) {
          for (let i = 0; i < selectedFile.length; i++) {
            fileForm.append('files', selectedFile[i]);
          }
        } else {
          fileForm.append('files', selectedFile);
        }
        fileForm.append(
          'description',
          `${campaignRecord?.name || 'Template'} file for WatConnect`
        );

        const attachmentRes = await attachmentsApi.post(
          `/${selectedTemplate.id}`,
          fileForm
        );
        const fileResult = attachmentRes?.data;
        const filesArray = fileResult?.records || fileResult?.data;
        if (filesArray?.[0]?.id) {
          finalParams.file_id = filesArray[0].id;
          finalParams.file_title = filesArray[0].file_name || filesArray[0].title;
          finalParams.file_url = filesArray[0].file_url;
          finalParams.file_name = filesArray[0].file_name || filesArray[0].title;
          finalParams.file_type = filesArray[0].filetype || filesArray[0].file_type;
          finalParams.mime_type = filesArray[0].mime_type;
        }
      }
      await onSendComplete?.({
        selectedTemplate,
        selectedWhatsAppSetting,
        parameterValues: finalParams,
        selectedFile,
      });
    } catch (err) {
      console.error('Error sending template:', err);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className='wa-modal-overlay'>
      <div className='wa-modal template-modal clean-modal'>
        <div className='wa-file-preview-header'>
          <div className='fw-bold fa-2x'>
            Send Template
          </div>
          <button className='wa-add-more-btn' onClick={onClose} title='Close'>
            ✕
          </button>
        </div>
        <div className='wa-modal-body scrollable'>
          <div className='modal-inner-content'>
            {loading ? (
              <div className='loading-state'>
                <p>Loading templates...</p>
              </div>
            ) : (
              <>
                <div className='template-section'>
                  <label className='form-label'>Select Category:</label>
                  <div className='category-buttons'>
                    {categories.map(category => (
                      <button
                        key={category}
                        className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>

                <div className='template-section'>
                  <label className='form-label'>Select Template:</label>
                  <select
                    className='form-select'
                    value={selectedTemplate?.id || ''}
                    onChange={e => {
                      const selected = filteredTemplates.find(
                        t => t.id === e.target.value
                      );
                      handleTemplateSelect(selected || null);
                    }}
                  >
                    <option value=''>-- Choose Template --</option>
                    {filteredTemplates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.category})
                      </option>
                    ))}
                  </select>
                </div>

                {selectedTemplate && (
                  <div className='template-content'>
                    <div className='parameter-box'>
                      <h6>Template Parameters</h6>
                      <div className='parameters-container'>
                        {renderParameterInputs()}
                        {/* {renderFileUploads()} */}
                      </div>
                      {!areAllParametersFilled() && (
                        <div className='parameter-warning'>
                          ⚠️ Please enter a fallback value for all parameters.
                        </div>
                      )}
                    </div>

                    <div className='preview-box'>
                      <h6>WhatsApp Preview</h6>
                      {renderWhatsAppPreview()}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className='wa-modal-footer'>
          <button className='btn btn-secondary' onClick={onClose}>
            Cancel
          </button>
          <button
            className='btn btn-primary'
            onClick={handleSendTemplate}
            disabled={sending || !selectedTemplate || !areAllParametersFilled()}
          >
            {sending ? (
              <>
                <span className='loading-spinner'></span>
                Sending...
              </>
            ) : (
              'Send Template'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;
